import { useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";

const ENTITLED_STATUSES = new Set(["trialing", "active", "manual", "past_due"]);

const TIERS = [
  {
    tier: "basic",
    name: "Basic",
    price: "£14.99",
    features: [
      "Receipt & voice capture",
      "Ingredient store with price history",
      "Recipe cost builder",
      "Price floor calculator",
      "Drift alerts",
    ],
  },
  {
    tier: "pro",
    name: "Pro",
    price: "£29.99",
    highlight: true,
    features: [
      "Everything in Basic",
      "Stock on hand & valuation",
      "Waste logging",
      "Low-stock reorder alerts",
      "Ongoing food-cost % tracking",
    ],
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const [busyTier, setBusyTier] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);

  const isEntitled = ENTITLED_STATUSES.has(user?.subscription_status);

  const startCheckout = async (tier) => {
    setBusyTier(tier);
    try {
      const { data } = await api.post("/billing/checkout", { tier });
      trackEvent("trial-started", { method: "checkout" });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusyTier(null);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPortalBusy(false);
    }
  };

  return (
    <div className="space-y-8 animate-rise" data-testid="pricing-page">
      <div className="text-center max-w-lg mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Plans</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-1">
          {isEntitled ? "Your plan" : "Start your 14-day free trial"}
        </h1>
        <p className="text-ink/60 mt-3">
          {isEntitled
            ? "Manage your plan and payment details below."
            : "Cost up to 2 real recipes free for 14 days. No card required. Cancel anytime."}
        </p>
      </div>

      {isEntitled ? (
        <div className="bg-surface border border-black/5 rounded-lg p-6 max-w-lg mx-auto space-y-4" data-testid="pricing-current-plan">
          <div>
            <p className="font-heading font-semibold text-xl">{user.tier === "pro" ? "Pro" : "Basic"} plan</p>
            {user.subscription_status === "trialing" && user.trial_ends_at && (
              <p className="text-sm text-ink/60 mt-1">Trial ends {new Date(user.trial_ends_at).toLocaleDateString("en-GB")}</p>
            )}
          </div>
          <button onClick={openPortal} disabled={portalBusy} data-testid="pricing-add-payment-method"
            className="w-full h-12 rounded-lg bg-tea hover:bg-tea-hover text-tea-text font-medium transition-colors duration-200 disabled:opacity-60">
            {portalBusy ? "Redirecting…" : "Add payment method"}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
          {TIERS.map((t) => (
            <div key={t.tier} data-testid={`pricing-tier-${t.tier}`}
              className={`rounded-lg p-6 flex flex-col gap-4 ${t.highlight ? "bg-ink text-white" : "bg-surface border border-black/5"}`}>
              <div>
                <p className={`font-heading font-bold text-2xl ${t.highlight ? "" : "text-ink"}`}>{t.name}</p>
                <p className="font-mono-num text-3xl font-semibold mt-1">
                  {t.price}<span className={`text-sm font-body font-normal ${t.highlight ? "text-white/70" : "text-ink/50"}`}>/month</span>
                </p>
              </div>
              <ul className="space-y-2 text-sm flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={16} className={`mt-0.5 shrink-0 ${t.highlight ? "text-tea" : "text-tea-text"}`} />
                    <span className={t.highlight ? "text-white/90" : "text-ink/80"}>{f}</span>
                  </li>
                ))}
              </ul>
              <button data-testid={`pricing-start-${t.tier}`} disabled={busyTier !== null}
                onClick={() => startCheckout(t.tier)}
                className={`w-full h-12 rounded-lg font-medium transition-colors duration-200 disabled:opacity-60 ${
                  t.highlight ? "bg-tea hover:bg-tea-hover text-tea-text" : "bg-ink text-cream hover:bg-ink/90"}`}>
                {busyTier === t.tier ? "Redirecting…" : "Start 14-day trial"}
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-center text-xs text-ink/40 max-w-lg mx-auto" data-testid="pricing-invoicing-note">
        Invoicing is free on every plan · send up to 3 client payments a month (card, Klarna, Clearpay) before you subscribe, unlimited once you're on Basic or Pro.
      </p>
    </div>
  );
}
